import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { API_CONFIG, ApiConfig } from '../api-config';
import {
  formatLadaSelectValue,
  resolveCountryFlagIconUrl,
} from '../../utils/country-flag.util';
import { GeneralCatalogSelectService } from '../settings/general-catalog-select.service';

/**
 * Shape mínima de una opción de select. Se define localmente en lugar de
 * importar de `@ui-library` para mantener la dirección de dependencias entre
 * capas: `shared-data-access` no debe depender de libs de UI.
 */
export interface ClientContactSelectOption {
  value: string;
  label: string;
  iconUrl?: string;
}

/**
 * Catálogos de selects para contactos/teléfonos de cliente.
 * Teléfonos/modelos vía general-catalogs; compañías vía phone-providers del país; ladas en `/selects/ladas`.
 */
@Injectable({ providedIn: 'root' })
export class ClientContactSelectsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly generalCatalogSelect = inject(GeneralCatalogSelectService);

  private readonly cache = new Map<string, ClientContactSelectOption[]>();

  getLadas(): Observable<ClientContactSelectOption[]> {
    return this.getLadaOptions('/selects/ladas');
  }

  getTypePhones(): Observable<ClientContactSelectOption[]> {
    return this.fromGeneralCatalog('phone_purposes');
  }

  getModelPhones(): Observable<ClientContactSelectOption[]> {
    return this.fromGeneralCatalog('phone_models');
  }

  /**
   * Proveedores telefónicos del país de residencia (o nacionalidad).
   * GET /selects/phone-providers?countryId=
   */
  getPhoneCompanies(
    countryId: string | null | undefined,
  ): Observable<ClientContactSelectOption[]> {
    const id = countryId?.trim() ?? '';
    if (!id) return of([]);

    const cacheKey = `phone-providers:${id}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return of(cached);

    return this.httpClient
      .get(
        `${this.apiConfig.apiUrl}/selects/phone-providers?countryId=${encodeURIComponent(id)}`,
        { observe: 'response', responseType: 'text' },
      )
      .pipe(
        map((resp) => this.parsePhoneProviders(resp.body)),
        tap((options) => this.cache.set(cacheKey, options)),
        catchError(() => of([])),
      );
  }

  private parsePhoneProviders(body: unknown): ClientContactSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id?: string; text?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: String(item.id ?? ''),
        label: item.text?.trim() || '',
      }));
    } catch {
      return [];
    }
  }

  private fromGeneralCatalog(
    catalogPrefix: string,
  ): Observable<ClientContactSelectOption[]> {
    const cacheKey = `gc:${catalogPrefix}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return of(cached);

    return this.generalCatalogSelect.getListOptionsByPrefix(catalogPrefix).pipe(
      map((options) =>
        options.map((o) => ({
          value: o.value,
          label: o.label,
        })),
      ),
      tap((options) => this.cache.set(cacheKey, options)),
      catchError(() => of([])),
    );
  }

  private getLadaOptions(
    endpointUrl: string,
  ): Observable<ClientContactSelectOption[]> {
    const cached = this.cache.get(endpointUrl);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${endpointUrl}`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseLadas(resp.body)),
        tap((options) => this.cache.set(endpointUrl, options)),
        catchError(() => of([])),
      );
  }

  private parseLadas(body: unknown): ClientContactSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{
          id?: string;
          code?: string;
          text?: string;
          label?: string;
          iconUrl?: string;
        }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => {
        const value = formatLadaSelectValue(String(item.id ?? ''));
        const iso = item.code?.trim().toUpperCase();
        return {
          value,
          label: item.text?.trim() || item.label?.trim() || value,
          iconUrl: resolveCountryFlagIconUrl({
            iso,
            iconUrl: item.iconUrl,
          }),
        };
      });
    } catch {
      return [];
    }
  }
}
